import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { OrderStatus } from '../../types';
import { useGetActiveManufacturersQuery } from '../manufacturer/manufacturers-api-slice';
import { getAllStatusIcons } from './order-status-icons';

interface DashboardFiltersProps {
  isAdmin: boolean;
  selectedManufacturerId?: number;
  onManufacturerChange: (id: number | undefined) => void;
  pastDueOnly: boolean;
  onPastDueChange: (value: boolean) => void;
  dueWithin7Days: boolean;
  onDueWithin7DaysChange: (value: boolean) => void;
  showPONumbers: boolean;
  onShowPONumbersChange: (value: boolean) => void;
  selectedStatuses: OrderStatus[];
  onStatusesChange: (statuses: OrderStatus[]) => void;
  viewMode: 'month' | 'week';
  onViewModeChange: (
    event: React.MouseEvent<HTMLElement>,
    newMode: 'month' | 'week' | null
  ) => void;
}

export const DashboardFilters = ({
  isAdmin,
  selectedManufacturerId,
  onManufacturerChange,
  pastDueOnly,
  onPastDueChange,
  dueWithin7Days,
  onDueWithin7DaysChange,
  showPONumbers,
  onShowPONumbersChange,
  selectedStatuses,
  onStatusesChange,
  viewMode,
  onViewModeChange,
}: DashboardFiltersProps) => {
  const statusIcons = getAllStatusIcons();
  const { data: manufacturers = [] } = useGetActiveManufacturersQuery(undefined, {
    skip: !isAdmin,
  });

  const handleStatusToggle = (status: OrderStatus) => {
    if (selectedStatuses.includes(status)) {
      onStatusesChange(selectedStatuses.filter(s => s !== status));
    } else {
      onStatusesChange([...selectedStatuses, status]);
    }
  };

  return (
    <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        {/* Manufacturer Filter - Admin Only */}
        {isAdmin && (
          <FormControl size="small" sx={{ minWidth: 200 }} required>
            <InputLabel>Manufacturer</InputLabel>
            <Select
              value={selectedManufacturerId || ''}
              label="Manufacturer"
              onChange={e =>
                onManufacturerChange(e.target.value ? Number(e.target.value) : undefined)
              }
              MenuProps={{
                disablePortal: false, // Ensure it uses portal (default)
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    zIndex: 9999, // Very high z-index to ensure it's above calendar
                  },
                },
                sx: {
                  zIndex: 9999,
                },
                style: {
                  zIndex: 9999,
                },
              }}
            >
              {manufacturers.map(manufacturer => (
                <MenuItem key={manufacturer.id} value={manufacturer.id}>
                  {manufacturer.licenseeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* View Mode Toggle */}
        <Box sx={{ mr: 2 }}>
          <ToggleButtonGroup value={viewMode} exclusive onChange={onViewModeChange} size="small">
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Past Due Filter */}
        <FormControlLabel
          control={
            <Switch
              checked={pastDueOnly}
              onChange={e => onPastDueChange(e.target.checked)}
              size="small"
            />
          }
          label="Past Due"
        />

        {/* Days <= 7 Filter */}
        <FormControlLabel
          control={
            <Switch
              checked={dueWithin7Days}
              onChange={e => onDueWithin7DaysChange(e.target.checked)}
              size="small"
            />
          }
          label="Due < 7 days"
        />

        {/* PO# vs Order# Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={showPONumbers}
              onChange={e => onShowPONumbersChange(e.target.checked)}
              size="small"
            />
          }
          label="Show PO#"
        />

        {/* Status Filters */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
            Status:
          </Box>
          {statusIcons.map(({ status, icon, label }) => (
            <Chip
              key={status}
              label={`${icon} ${label}`}
              onClick={() => handleStatusToggle(status)}
              color={selectedStatuses.includes(status) ? 'primary' : 'default'}
              size="small"
              variant={selectedStatuses.includes(status) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Box>
    </Paper>
  );
};
